import {AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors} from "@angular/forms";

export class CustomValidators{

  /**
   * Validates a form control's value against a provided regular expression pattern
   *
   * Note: Do not use regular expressions with the global 'g' flag ('/pattern/g') as
   * it maya cause unexpected behavior due to 'RegExp.test()' stateful behavior
   *
   * @param pattern Regular expression to test the control's value against.
   * @param error Error object to return when the validation fails
   * @returns Validator function returning the error if validation fails, else null
   */
  static patternValidator(pattern: RegExp, error: { [key: string]: boolean }) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value && !pattern.test(control.value)) {
        return error;
      }
      return null;
    };
  }

  /**
   * Validates that the 'password' and 'passwordConfirm' fields inside a form group match.
   *
   * This should be applied to a FormGroup rather than an individual control.
   *
   * If hte passwords do not match, it sets the 'NoPasswordMatch' error on the 'passwordConfirm' control.
   *
   * @param control Form group containing 'password' and 'passwordConfirmation' controls
   */
  static passwordMatchValidator(control: AbstractControl) {
    const password: string = control.get('password').value;
    const confirmPassword: string = control.get('passwordConfirm').value;
    if (password !== confirmPassword) {
      control.get('passwordConfirm').setErrors({ NoPassswordMatch: true });
    }
  }

  /**
   * Custom form validator that ensures at least one control in a FormGroup is selected.
   *
   * Iterates through all controls inside the given group and checks whether any of them has a truthy value (e.g. a checkbox marked 'true').
   *
   * @param group FormGroup containing multiple controls (e.g. checkboxes)
   * @return null if at least one control is selected (valid), else {required: true} to indicate a validation error
   */
  static atLeastOneSelectedValidator = (group: UntypedFormGroup) => {
    const anySelected = Object.values(group.controls).some((ctrl: UntypedFormControl) => !!ctrl.value);
    return anySelected ? null : { required: true };
  }
}
